/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { useState } from 'react'
import Box from '@mui/material/Box'
import { FormProvider, useForm, type SubmitHandler } from 'react-hook-form'
import { getAcademicUnitDepth, getSlotLoading } from '../../../../store/slices/distributionPlanSlice/selectors'
import GenericDateTimePicker from '../../../../components/GenericDateTimePicker'
import { yupResolver } from '@hookform/resolvers/yup'
import { type InstructorAddedQuestions, SlotStatus, type ISlot, type ISurveyTemplate, type AcademicLevelAddedQuestions } from '../../../../models/DistributionPlan'
import { useTheme } from '@mui/material/styles'
import DefaultModalActions from '../../../../components/GenericModal/DefaultModalActions'
import ValidationAlert from '../../../../components/ValidationAlert'
import { isBefore } from 'date-fns'
import { useNavigate } from 'react-router-dom'
import { getDirtyValues } from '../../../../utils/onlyUpdatedFields'
import { ApiAutoComplete } from '../../../../components/FormElements/ApiAutoComplete'
import ROUTES from '../../../../consts/paths'
import FormSwitch from '../../../../components/FormElements/FormSwitch'
import FormInput from '../../../../components/FormElements/FormInput'
import { Divider, Typography } from '@mui/material'
import FormCheckbox from '../../../../components/FormElements/FormCheckBox'
import AnimatedContainer from '../../../../components/Animations/AnimatedContainer'
import { grey } from '../../../../colors'
import MultiSelectApiAutoComplete from '../../../../components/FormElements/MultiSelectApiAutoComplete'
import { distributionFormSchema } from '../schema'
import { type IAcademicUnit } from '../../../../models/AcademicUnit'
import { ReleaseMode } from '../../../../helpers/parseReleaseTimestamp'
import { SurveyProjectTypeAndRollingBasisToggle } from './SurveyProjectTypeAndRollingBasisToggle'
import { useMutation } from '@tanstack/react-query'
import { DistributionManager } from '../../../../APIManager'

interface FormType {
  templateId: string
  termId: string
  startTimestamp: Date | null
  endTimestamp: Date | null
  releaseTimestamp: Date | null
  isRollingResultsEnabled: boolean
  isInstructorAddedQuestionsAllowed?: boolean | null
  instructorAddedQuestions?: InstructorAddedQuestions | null
  isAcademicLevelAddedQuestionsAllowed?: boolean | null
  academicLevelAddedQuestions?: AcademicLevelAddedQuestions | null
  isSlotAnonymous: boolean
}

type ModalStep = 'TOGGLE' | 'FORM'
const STEP: Record<ModalStep, ModalStep> = { TOGGLE: 'TOGGLE', FORM: 'FORM' }

interface CreateDistributionProps {
  selectedRow?: ISlot & Pick<ISurveyTemplate, 'templateRevision'> | Record<string, any>
  projectId?: string
  handleCloseModal?: () => void
  handleAddSlot?: (slotId: string) => void
}
export const CreateAndEditDistributionPlan: React.FC<CreateDistributionProps> = ({
  selectedRow,
  projectId = '',
  handleCloseModal = () => { },
  handleAddSlot
}) => {
  const [errors, setErrors] = useState<unknown>({})
  const theme = useTheme()
  const navigate = useNavigate()
  const isEditAction = Boolean(selectedRow)
  const loading = getSlotLoading()
  const [isCreating, setIsCreating] = useState(false)
  const [step, setStep] = useState<ModalStep>(STEP.TOGGLE)

  const {
    releaseTimestamp,
    slotNodeDetails,
    instructorAddedQuestions,
    isInstructorAddedQuestionsAllowed,
    isAcademicLevelAddedQuestionsAllowed,
    academicLevelAddedQuestions
  } = (selectedRow as any) ?? {}

  const isDisabled = Boolean(selectedRow && slotNodeDetails?.slotStatus !== SlotStatus.Draft)
  const isSlotStatusLive = isDisabled
  const academicUnitList = getAcademicUnitDepth() || []
  const academicUnitDepth = academicUnitList[0]?.depth ?? 0

  const methods = useForm<any>({
    resolver: yupResolver(distributionFormSchema),
    mode: 'onChange',
    defaultValues: {
      templateId: selectedRow?.surveyTemplate?.templateId ?? '',
      termId: selectedRow?.term?.termId ?? '',
      startTimestamp: selectedRow?.slotStartTimestamp ?? null,
      endTimestamp: selectedRow?.slotEndTimestamp ?? null,
      releaseTimestamp: selectedRow?.releaseTimestamp ?? null,
      isRollingResultsEnabled: selectedRow?.isRollingResultsEnabled ?? false,
      isSlotAnonymous: selectedRow?.isSlotAnonymous ?? false,
      isInstructorAddedQuestionsAllowed: isInstructorAddedQuestionsAllowed ?? false,
      instructorAddedQuestions: instructorAddedQuestions ?? {
        questionCount: null,
        isCustomQuestionAllowed: false
      },
      isAcademicLevelAddedQuestionsAllowed: isAcademicLevelAddedQuestionsAllowed ?? false,
      academicLevelAddedQuestions: academicLevelAddedQuestions ?? {
        academicUnitCategories: [],
        questionCount: null,
        isCustomQuestionAllowed: false
      }
    }
  })

  const { handleSubmit, control, watch, formState: { isDirty, dirtyFields } } = methods as any

  const editSlotMutation = useMutation({
    mutationFn: async ({ slotId, data }: { slotId: string, data: any }) =>
      await DistributionManager.updateDistributionPlan(slotId, data)
  })

  const createSlotMutation = useMutation({
    mutationFn: async ({ projectId, data }: { projectId: string, data: any }) =>
      await DistributionManager.createDistributionPlan(projectId, data)
  })

  const onSubmit: SubmitHandler<FormType> = async (value) => {
    const {
      templateId,
      termId,
      startTimestamp,
      endTimestamp,
      releaseTimestamp,
      isRollingResultsEnabled,
      isInstructorAddedQuestionsAllowed,
      instructorAddedQuestions,
      isAcademicLevelAddedQuestionsAllowed,
      academicLevelAddedQuestions,
      isSlotAnonymous
    } = value

    const isEditing = isEditAction
    const selectedTemplateRevision = (selectedRow as any)?.surveyTemplate?.templateRevision
    const templateRevisionToUpdate = isEditing
      ? Number(selectedTemplateRevision ?? 1)
      : 1

    const slotData = {
      termId,
      templateId,
      templateRevision: templateRevisionToUpdate,
      startTimestamp,
      endTimestamp,
      ...(!isRollingResultsEnabled && { releaseTimestamp }),
      isRollingResultsEnabled,
      isSlotAnonymous,
      isInstructorAddedQuestionsAllowed,
      instructorAddedQuestions,
      isAcademicLevelAddedQuestionsAllowed,
      academicLevelAddedQuestions: {
        ...academicLevelAddedQuestions,
        academicUnitCategories: academicLevelAddedQuestions?.academicUnitCategories.map(
          (academic: IAcademicUnit) => ({
            categoryId: academic.categoryId,
            categoryName: academic.categoryName
          })
        )
      }
    }

    const updatedVals = getDirtyValues(dirtyFields, slotData)
    setIsCreating(true)
    try {
      if (isEditing) {
        await editSlotMutation.mutateAsync({ slotId: selectedRow?.slotId, data: updatedVals })
      } else {
        const response = await createSlotMutation.mutateAsync({ projectId, data: slotData })
        if (response.data.slotId && !handleAddSlot) {
          navigate(`${ROUTES.COURSE_AND_ENROLLMENT}/${response.data.slotId}`)
        }
        handleAddSlot?.(response.data.slotId)
        await DistributionManager.getDistributionById(projectId)
      }
      handleCloseModal()
    } catch (err: any) {
      setErrors(err?.message || 'Unknown error')
    } finally {
      setIsCreating(false)
    }
  }

  const isDateInPast = (date: Date): boolean => date && isBefore(new Date(date), new Date())
  const isSlotInDraft = slotNodeDetails?.slotStatus === SlotStatus.Draft
  const isSlotInPublished = slotNodeDetails?.slotStatus === SlotStatus.Published
  const isSlotInLive = slotNodeDetails?.slotStatus === SlotStatus.Live
  const isSlotInClosed = slotNodeDetails?.slotStatus === SlotStatus.Closed
  const isAcademicLevelAddedQuestions = watch('isAcademicLevelAddedQuestionsAllowed') ?? false
  const isInstructorAddedQuestions = watch('isInstructorAddedQuestionsAllowed')
  const isRollingOn = watch('isRollingResultsEnabled')
  const isAcademicUnitsCustomQuestionAllowed = watch('academicLevelAddedQuestions.isCustomQuestionAllowed')
  const isInstructorCustomQuestionAllowed = watch('instructorAddedQuestions.isCustomQuestionAllowed')

  const isOptOutAllowed = watch('isOptOutAllowed') ?? false
  const isInstructorOptOutAllowed = watch('isInstructorOptOutAllowed') ?? false
  const canEditStartTimestamp = !isEditAction || isSlotInDraft || isSlotInPublished
  const canEditEndTimestamp = !isEditAction || isSlotInDraft || isSlotInPublished || isSlotInLive
  const canEditReleaseTimestamp = !isEditAction || isSlotInDraft || isSlotInPublished || isSlotInLive ||
    (isSlotInClosed && !isDateInPast(releaseTimestamp ?? new Date()))

  return (
    <>
      {step === STEP.TOGGLE && !isEditAction
        ? (
          <FormProvider {...methods}>
            <SurveyProjectTypeAndRollingBasisToggle
              handleCloseModal={handleCloseModal}
              loading={loading}
              isCreating={isCreating}
              isSlotStatusLive={isSlotStatusLive}
              setStep={setStep}
            />
          </FormProvider>
          )
        : (
          <FormProvider {...methods}>
            <ValidationAlert errors={errors} />
            <form onSubmit={handleSubmit(onSubmit)}>
              <Box display="flex" flexDirection="column" gap={theme.spacing(6)}>
                <ApiAutoComplete
                  apiEndpoint="/api/terms/summary/"
                  labelField="termName"
                  label="Term"
                  name="termId"
                  required

                  selectedItem={selectedRow?.term}
                  readOnly={isDisabled}
                  placeholder="Select term"
                  apiParams={{
                    slotId: selectedRow?.slotId,
                    ...(!isEditAction && {
                      distributionSeriesId: projectId,
                      isPresent: false
                    })
                  }}
                />
                <ApiAutoComplete
                  apiEndpoint="/api/templates/summary/"
                  label="Survey Template"
                  labelField="title"
                  name="templateId"
                  required
                  selectedItem={selectedRow?.surveyTemplate}
                  readOnly={isDisabled}
                  placeholder="Select survey template"
                />
                <GenericDateTimePicker
                  name="startTimestamp"
                  label="Default Survey Start Date"
                  required
                  minDate={new Date()}
                  disabled={!canEditStartTimestamp}
                />

                <GenericDateTimePicker
                  name="endTimestamp"
                  label="Default Survey End Date"
                  required
                  minDate={new Date()}
                  disabled={!canEditEndTimestamp}
                />
                {(!isRollingOn && selectedRow?.releaseTimestamp !== ReleaseMode.Rolling) && (
                  <GenericDateTimePicker
                    name="releaseTimestamp"
                    label="Default Report Release Date"
                    required
                    minDate={new Date()}
                    disabled={!canEditReleaseTimestamp}
                  />
                )}

                   {/* OPT OUT PERMISSION */}
                <Divider />
                <Box>
                  <Typography mb={2} color={grey[850]} fontSize="18px" fontWeight={500}>
                    Opt out permissions
                  </Typography>
                  <FormSwitch
                    name="isOptOutAllowed"
                    label="Allow Academic Units to Opt out"
                    sx={{ mb: 2 }}
                    control={control}
                    disabled={isSlotStatusLive}
                    checked={isOptOutAllowed || false}
                  />
                  <AnimatedContainer isVisible={isOptOutAllowed}>
                    <Box>
                      <MultiSelectApiAutoComplete
                        apiEndpoint={`/api/acad_units/depth/${academicUnitDepth}/children/summary/`}
                        label="Academic Unit level(s):"
                        placeholder="Select Academic Unit Levels"
                        name="optOut.academicUnitCategories"
                        getOptionLabel={(c: IAcademicUnit) => c.categoryName}
                        getOptionValue={(c: IAcademicUnit) => c.categoryId}
                        getOptionDisabled={(c: IAcademicUnit) => c.categoryName === 'University'}
                        disabled={isSlotStatusLive}
                      />
                      <Typography my={2} fontSize="16px" color={grey[850]}>
                        Admins at the selected Academic Unit levels will be able to opt out their academic units and sections.
                      </Typography>
                      <Typography mb={1} mt={1} fontSize="16px" color={grey[850]} fontWeight={500}>
                        Opt-out Deadline:
                      </Typography>
                      <Box display={'flex'} gap={2} >
                        <FormInput
                          control={control}
                          name="optOut.questionCount"
                          placeholder="Days"
                          type="number"
                          disabled={isSlotStatusLive}
                          wrapperStyle={{ width: '120px' }}
                        />
                        <Typography fontSize="16px" color={grey[850]} mt={2}>
                          days before Section Survey Start Date
                        </Typography>
                      </Box>

                    </Box>
                  </AnimatedContainer>
                  {isAcademicLevelAddedQuestions && <Divider sx={{ my: 4 }} />}
                  <FormSwitch
                    name="isInstructorOptOutAllowed"
                    label="Allow Instructors to Opt out"
                    sx={{ mb: 2 }}
                    control={control}
                    disabled={isSlotStatusLive}
                    checked={isInstructorOptOutAllowed || false}

                  />
                  <AnimatedContainer isVisible={isInstructorOptOutAllowed} >
                    <Typography my={1} fontSize="16px" color={grey[850]} fontWeight={500}>
                      Opt-out Deadline:
                    </Typography>
                    <Box display={'flex'} gap={2} >
                      <FormInput
                        control={control}
                        name="optOut.questionCount"
                        placeholder="Days"
                        type="number"
                        disabled={isSlotStatusLive}
                        wrapperStyle={{ width: '120px' }}
                      />
                      <Typography fontSize="16px" color={grey[850]} mt={2}>
                        days before Section Survey Start Date
                      </Typography>
                    </Box>
                  </AnimatedContainer>
                </Box>
                <Divider />

                {/* CUSTOM QUESTIONS */}
                <Box>
                  <Typography mb={2} color={grey[850]} fontSize="18px" fontWeight={500}>
                    Custom Questions
                  </Typography>
                  <FormSwitch
                    name="isAcademicLevelAddedQuestionsAllowed"
                    label="Allow Academic Units to add questions"
                    sx={{ mb: 2 }}
                    control={control}
                    disabled={isSlotStatusLive}
                    checked={isAcademicLevelAddedQuestions || false}
                  />
                  <AnimatedContainer isVisible={isAcademicLevelAddedQuestions}>
                    <Box>
                      <MultiSelectApiAutoComplete
                        apiEndpoint={`/api/acad_units/depth/${academicUnitDepth}/children/summary/`}
                        label="Academic Unit level(s)"
                        placeholder="Select Academic Unit(s)"
                        name="academicLevelAddedQuestions.academicUnitCategories"
                        getOptionLabel={(c: IAcademicUnit) => c.categoryName}
                        getOptionValue={(c: IAcademicUnit) => c.categoryId}
                        getOptionDisabled={(c: IAcademicUnit) => c.categoryName === 'University'}
                        disabled={isSlotStatusLive}
                      />
                      <Typography maxWidth={420} mb={2} fontSize="16px" color={grey[850]}>
                        Admins at the selected Academic Unit levels will be able to add questions on this survey
                      </Typography>
                      <FormInput
                        control={control}
                        name="academicLevelAddedQuestions.questionCount"
                        placeholder="Number of questions"
                        type="number"
                        disabled={isSlotStatusLive}
                        label="Number of questions"
                      />
                      <Typography mb={1} mt={1} fontSize="16px" color={grey[850]}>
                        Maximum number of questions an admin can add.
                      </Typography>
                      <FormCheckbox
                        control={control}
                        name="academicLevelAddedQuestions.isCustomQuestionAllowed"
                        label="Allow creation of new custom questions"
                        sx={{ ml: -3, fontSize: '16px' }}
                        disabled={isSlotStatusLive}
                        onChange={(e) => {
                          methods.setValue(
                            'academicLevelAddedQuestions.isCustomQuestionAllowed',
                            e.target.checked,
                            { shouldDirty: true }
                          )
                        }}
                        checked={isAcademicUnitsCustomQuestionAllowed || false}
                      />
                      <Typography mt={-1} ml={8} mb={1} fontSize="16px" color={grey[850]}>
                        Admin will be able to create their own questions.
                      </Typography>
                    </Box>
                  </AnimatedContainer>
                  {isAcademicLevelAddedQuestions && <Divider sx={{ my: 4 }} />}
                  <FormSwitch
                    name="isInstructorAddedQuestionsAllowed"
                    label="Allow Instructor to add questions"
                    sx={{ mb: 2 }}
                    control={control}
                    disabled={isSlotStatusLive}
                    checked={isInstructorAddedQuestions || false}
                  />
                  <AnimatedContainer isVisible={isInstructorAddedQuestions}>
                    <Box>
                      <FormInput
                        control={control}
                        name="instructorAddedQuestions.questionCount"
                        placeholder="Number of questions"
                        type="number"
                        disabled={isSlotStatusLive}
                        label="Number of questions"
                      />
                      <Typography color={grey[850]} mt={1} fontSize="16px">
                        Maximum questions instructors can add.
                      </Typography>
                      <FormCheckbox
                        control={control}
                        name="instructorAddedQuestions.isCustomQuestionAllowed"
                        label="Allow creation of new custom questions"
                        sx={{ ml: -3, fontSize: '16px' }}
                        disabled={isSlotStatusLive}
                        onChange={(e) => {
                          methods.setValue(
                            'instructorAddedQuestions.isCustomQuestionAllowed',
                            e.target.checked,
                            { shouldDirty: true }
                          )
                        }}
                        checked={isInstructorCustomQuestionAllowed || false}
                      />
                      <Typography ml={8} mt={-1} fontSize="16px" color={grey[850]}>
                        Instructors will be able to create their own questions.
                      </Typography>
                    </Box>
                  </AnimatedContainer>
                </Box>

              </Box>
              <DefaultModalActions
                destructiveButtonProps={{ onClick: handleCloseModal, disabled: loading || isCreating }}
                confirmButtonProps={{
                  text: isEditAction ? 'Save' : 'Create',
                  loading: loading || isCreating,
                  type: 'submit',
                  disabled: isEditAction && !isDirty
                }}
              />
            </form>
          </FormProvider>
          )}
    </>
  )
}
